// components/InputField.js
"use client";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Controller } from "react-hook-form";

import { clearFieldErrors } from "@/lib/redux/slices/formSlice";
import SvgIcon from "@/lib/icons/SvgIcon";
import getNestedError from "@/lib/utils/getNestedError";

function InputField({
  label,
  name,
  type = "text",
  control,
  setValue,
  clearErrors,
  errors = null,
  className,
  onChange,
  float,
  multiline = false,
  register,
  max,
  min,
  active = 1,
  labelPosition = "top",
  fieldHeight = "medium",
  fieldWidth = undefined, // undefined = full width (for form builder), or "small"/"medium"/"large" for customer form
  fieldAlignment = "left", // "left", "center", or "right"
  labelColor = null,
  ...props
}) {
  
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  // Normalize active (0/1 → boolean)
  const isActive = Number(active) === 1;

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const renderInput = (field) => {
    const handleChange = (e) => {
      // Controller's field.onChange will update the form state with the correct field name
      // The Controller is registered with the 'name' prop, so field.onChange will update that field
      field.onChange(e);

      // Clear errors for this specific field using the name prop
      if (typeof clearErrors === "function" && name) {
        clearErrors(name);
      }
      if (name) {
        dispatch(clearFieldErrors(name));
      }
    };

    const errorMessage = getNestedError(errors, name);

    if (type === "date") {
      return (
        <input
          {...field}
          {...props}
          value={field.value || ""}
          type={type}
          onChange={(e) => {
            let value = e.target.value;

            // Optional: match pattern like dd/mm/yyyy
            const match = value.match(/^(\d{2})\/(\d{2})\/(\d{0,6})$/);
            if (match) {
              const day = match[1];
              const month = match[2];
              let year = match[3];

              // Limit year to 4 digits
              if (year.length > 4) {
                year = year.slice(0, 4);
                value = `${day}/${month}/${year}`;
                e.target.value = value;
              }
            }

            handleChange(e);
          }}
          id={name}
          className={`w-full ${heightClasses} text-black border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D45EF] autofill:!bg-white autofill:!shadow-[0_0_0px_40rem_white_inset]
          ${errorMessage ? "border-red-500" : "border-gray-300"} ${className} ${!isActive ? "opacity-50 pointer-events-none" : ""}`}
          placeholder={placeholderText}
          max={max}
          min={min}
        />
      );
    }

    if (type === "number") {
      return (
        <input
          {...field}
          {...props}
          value={field.value || ""} // Add default value
          type={"text"}
          onChange={(e) => {
            const regex = /^\d*\.?\d*$/;
            if (regex.test(e.target.value) || e.target.value === "") {
              handleChange(e);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "-" || e.key === "e") {
              e.preventDefault();
            }
          }}
          id={name}
          min={0}
          className={`w-full ${heightClasses} text-black border rounded-lg focus:outline-none focus:ring-2 autofill:!bg-white autofill:!shadow-[0_0_0px_40rem_white_inset] ${errorMessage ? "border-red-500 focus:ring-red-500/50" : "focus:ring-[#5D45EF]"} ${className} ${!isActive ? "opacity-50 pointer-events-none" : ""}`}
          placeholder={placeholderText}
        />
      );
    }

    if (type === "price") {
      return (
        <div className="relative">
          <div className="icon absolute top-0 left-0 px-4 py-2 min-h-[46px] text-lg flex items-center cursor-pointer text-black">
            <span>AED</span>
          </div>
          <input
            {...field}
            {...props}
            value={field.value || ""} // Add default value
            type={"text"}
            onChange={(e) => {
              const regex = /^\d*\.?\d*$/;
              if (regex.test(e.target.value) || e.target.value === "") {
                handleChange(e);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e") {
                e.preventDefault();
              }
            }}
            id={name}
            min={0}
            className={`w-full ${heightClasses} pl-14 text-black border rounded-lg focus:outline-none focus:ring-2 autofill:!bg-white autofill:!shadow-[0_0_0px_40rem_white_inset] 
              ${errorMessage ? "border-red-500 focus:ring-red-500/50" : "focus:ring-[#5D45EF]"} ${className} ${!isActive ? "opacity-50 pointer-events-none" : ""}`}
              placeholder={placeholderText}
          />
        </div>
      );
    }

    if (multiline === "true") {
      return (
        <textarea
          {...field}
          {...props}
          value={field.value || ""} // Add default value
          type="text"
          onChange={(e) => handleChange(e)}
          className={`block w-full ${heightClasses} text-black border rounded-lg 
        focus:outline-none focus:ring-2 autofill:!bg-white autofill:!shadow-[0_0_0px_40rem_white_inset] 
        ${errors && errors[name] ? "border-red-500 focus:ring-red-500/50" : "border-gray-300 focus:ring-[#5D45EF]"} 
        ${className} ${!isActive ? "opacity-50 pointer-events-none" : ""}`}
          placeholder={placeholderText}
        />
      );
    }

    return (
      <input
        {...field}
        {...props}
        value={field.value || ""} // Add default value
        onChange={onChange ? onChange : handleChange}
        type={type === "password" && showPassword ? "text" : type}
        id={name}
        className={`w-full ${heightClasses} text-black border rounded-lg focus:outline-none focus:ring-2  autofill:!bg-white autofill:!shadow-[0_0_0px_40rem_white_inset] 
        ${errorMessage ? "border-red-500 focus:ring-red-500/50" : "focus:ring-[#5D45EF] border-gray-300"} ${className} ${!isActive ? "opacity-50 pointer-events-none" : ""}`}
        placeholder={placeholderText}
      />
    );
  };

  // Get error message using the exact name prop to ensure correct field association
  const errorMessage = name ? getNestedError(errors, name) : null;
  
  // Debug: Log error for dynamic child fields to help diagnose issues
  if (name && name.startsWith("child_") && errors && errors[name]) {
      }

  // Get height classes based on fieldHeight prop
  const getHeightClasses = () => {
    switch (fieldHeight) {
      case "small":
        return "py-1.5 px-3 text-sm min-h-[36px]";
      case "large":
        return "py-3.5 px-4 text-base min-h-[56px]";
      case "medium":
      default:
        return "py-2 sm:py-2.5 px-4 text-sm sm:text-base min-h-[46px]";
    }
  };

  // Get width classes based on fieldWidth and fieldAlignment props
  // Only apply width restrictions if fieldWidth is explicitly provided
  // If not provided (form builder), use full width without restrictions
  const getWidthClasses = () => {
    if (!fieldWidth || fieldWidth === undefined) {
      return "w-full"; // Full width for form builder
    }
    
    // Get alignment margin class
    const alignmentMargin = fieldAlignment === "center" ? "mx-auto" : fieldAlignment === "right" ? "ml-auto" : "";
    
    switch (fieldWidth) {
      case "small":
        return `w-full max-w-sm ${alignmentMargin}`;
      case "large":
        return "w-full"; // Full width (matches container width)
      case "medium":
      default:
        return `w-full max-w-xl ${alignmentMargin}`;
    }
  };

  const heightClasses = getHeightClasses();
  const widthClasses = getWidthClasses();

  // Determine label rendering based on labelPosition
  const shouldShowLabel = label && float === "false" && labelPosition !== "placeholder";
  const shouldShowLabelTop = shouldShowLabel && labelPosition === "top";
  const shouldShowLabelLeft = shouldShowLabel && labelPosition === "left";
  // Only show placeholder when label position is "placeholder", otherwise no placeholder
  const placeholderText = labelPosition === "placeholder" && label ? label : (labelPosition === "placeholder" ? props.placeholder : "");

  const labelStyle = labelColor ? { color: labelColor } : { color: '#374151' }; // default text-gray-700

  return (
    <div className={`relative ${widthClasses}`}>
      {shouldShowLabelTop && (
        <label className="block text-sm sm:text-base mb-2 sm:mb-2.5" style={labelStyle} htmlFor={name}>
          {label}
        </label>
      )}
      <div className={shouldShowLabelLeft ? "flex items-center gap-3" : ""}>
        {shouldShowLabelLeft && (
          <label className="block text-sm sm:text-base whitespace-nowrap min-w-[120px]" style={labelStyle} htmlFor={name}>
            {label}
          </label>
        )}
        <div className={`relative ${shouldShowLabelLeft ? "flex-1 min-w-0" : ""}`}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => renderInput(field)}
          />

          {type === "password" && (
            <button
              type="button"
              onClick={handleTogglePassword}
              className={`absolute top-0 px-4 right-0 text-xl flex items-center cursor-pointer text-gray-500 ${fieldHeight === "small" ? "py-1.5 min-h-[36px]" : fieldHeight === "large" ? "py-3.5 min-h-[56px]" : "py-2.5 min-h-[46px]"}`}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <SvgIcon name="view-off" size="1em" />
              ) : (
                <SvgIcon name="view" size="1em" />
              )}
            </button>
          )}
        </div>
      </div>

      {errorMessage && (
        <p className={`text-red-500 text-sm mt-2.5 ${shouldShowLabelLeft ? "ml-[calc(120px+0.75rem)]" : ""}`}>
          {errorMessage}
        </p>
      )}
    </div>
  );
}

InputField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  className: PropTypes.string,
  float: PropTypes.string,
  multiline: PropTypes.bool,
  setValue: PropTypes.func,
  clearErrors: PropTypes.func,
  register: PropTypes.func,
  onChange: PropTypes.func,
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  active: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]), // added
  labelPosition: PropTypes.oneOf(["top", "left", "placeholder"]),
  fieldHeight: PropTypes.oneOf(["small", "medium", "large"]),
  fieldWidth: PropTypes.oneOf(["small", "medium", "large", undefined]),
};

export default InputField;
